import { Inject, LOCALE_ID, NgModule, Type } from '@angular/core';
import { CommonModule, registerLocaleData } from '@angular/common';
import { IcilaluneShellRoutingModule } from './icilalune-shell-routing.module';
import {
  NavigationActionTiming,
  StoreRouterConnectingModule,
} from '@ngrx/router-store';
import { APP_CONFIG } from '@madeinlune/ngx-app-config';
import { AppConfig } from '@icilalune/types';
import { IcilaluneRouterModule } from '@icilalune/router';
import { TranslocoService } from '@ngneat/transloco';
import { IcilaluneShellStateModule } from '@icilalune/shell-state';
import localeFr from '@angular/common/locales/fr';
import { DialogModule } from '@angular/cdk/dialog';
import { SHOW_THREEJS_STATS } from '@icilalune/three-tunnel/providers';
import { DataAccessModule } from '@nx-icilalune/data/access';
import {
  Agency,
  Blog,
  Client,
  IciLaLuneTypes,
  MainMenuItem,
  News,
  Page,
  ParagraphCode,
  ParagraphContentLinks,
  ParagraphMedia,
  ParagraphQuote,
  ParagraphText,
  Profile,
  Project,
  Solution,
  TechnologyTerm,
} from '@nx-icilalune/data/icilalune/types';
import {
  DrupalTerm,
  ImageMedia,
  SvgMedia,
  VimeoMedia,
} from '@nx-icilalune/drupal-types';
import {
  DATA_ACCESS_BASE_URL,
  JSON_API_LANGUAGE,
} from '@nx-icilalune/data/access/tokens';
import { TERMS_PROVIDERS } from './terms.providers';
import { PROJECT_PROVIDERS } from './project.providers';
import { MAIN_MENU_PROVIDERS } from './main-menu.providers';
import { ROUTE_PROVIDERS } from './route.providers';
import { THEME_PROVIDERS } from './theme.providers';
import { LIST_PROVIDERS } from './list.providers';
import { SECONDARY_MENU_PROVIDERS } from './secondary-menu.providers';
import { MainMenuGuard } from './main-menu.guard';
import { IcilaluneDialogModule } from '@madeinlune/dialog';
import { PwaModule } from '@nx-icilalune/icilalune/pwa';
import {
  SHELL_FEATURE_ENVIRONMENT,
  ShellFeatureEnvironment,
} from '@icilalune/shell-feature/tokens';

registerLocaleData(localeFr, 'fr');

const modelClassMap: Map<IciLaLuneTypes, Type<any>> = new Map<
  IciLaLuneTypes,
  Type<any>
>();
modelClassMap.set('projects', Project);
modelClassMap.set('articles', News);
modelClassMap.set('blogs', Blog);
modelClassMap.set('menuLinks', MainMenuItem);
modelClassMap.set('pages', Page);
modelClassMap.set('clients', Client);
modelClassMap.set('images', ImageMedia);
modelClassMap.set('media--vimeo', VimeoMedia);
modelClassMap.set('svgs', SvgMedia);
modelClassMap.set('term--business_unit', DrupalTerm);
modelClassMap.set('term--solutions', DrupalTerm);
modelClassMap.set('term--technologies', TechnologyTerm);
modelClassMap.set('term--domains', DrupalTerm);
modelClassMap.set('term--types', DrupalTerm);
modelClassMap.set('paragraph--paragraph_content_links', ParagraphContentLinks);
modelClassMap.set('paragraph--paragraph_texte', ParagraphText);
modelClassMap.set('paragraph--paragraph_code', ParagraphCode);
modelClassMap.set('paragraph--paragraph_media', ParagraphMedia);
modelClassMap.set('paragraph--paragraph_quote', ParagraphQuote);
modelClassMap.set('solutions', Solution);
modelClassMap.set('agencies', Agency);
modelClassMap.set('node--profile', Profile);

@NgModule({
  imports: [
    CommonModule,
    IcilaluneShellRoutingModule,
    IcilaluneRouterModule,
    IcilaluneShellStateModule,
    StoreRouterConnectingModule.forRoot({
      navigationActionTiming: NavigationActionTiming.PostActivation,
    }),
    DataAccessModule.forRoot(modelClassMap),
    DialogModule,
    PwaModule,
    IcilaluneDialogModule,
  ],
  providers: [
    MainMenuGuard,
    TERMS_PROVIDERS,
    PROJECT_PROVIDERS,
    MAIN_MENU_PROVIDERS,
    SECONDARY_MENU_PROVIDERS,
    ROUTE_PROVIDERS,
    THEME_PROVIDERS,
    LIST_PROVIDERS,
    {
      provide: DATA_ACCESS_BASE_URL,
      deps: [APP_CONFIG],
      useFactory: (appConfig: AppConfig) => {
        return appConfig.api.baseURL;
      },
    },
    {
      provide: JSON_API_LANGUAGE,
      deps: [TranslocoService],
      useFactory: (translocoService: TranslocoService) => {
        return translocoService.langChanges$;
      },
    },
    { provide: LOCALE_ID, useValue: 'fr' },
    {
      provide: SHOW_THREEJS_STATS,
      deps: [SHELL_FEATURE_ENVIRONMENT],
      useFactory: (env: ShellFeatureEnvironment) => {
        return !env.production;
      },
    },
  ],
})
export class IcilaluneShellFeatureModule {
  constructor(@Inject(APP_CONFIG) private appConfig: AppConfig) {}
}
